import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry for client-side error tracking.
 *
 * No-ops if VITE_SENTRY_DSN is not set, so local/dev builds don't ping Sentry.
 * Should be called BEFORE createRoot() in main.tsx so route errors caught
 * during the initial render are captured.
 *
 * The matching ErrorBoundary in src/components/ErrorBoundary.tsx also calls
 * Sentry.captureException directly so React render errors are tagged with
 * componentStack info even if they bypass the global handler.
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    // Intentionally quiet — don't spam dev logs for the missing-DSN case.
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE, // "development" | "production"
    // Conservative sample rates for early production. Bump after Phase 2
    // when we know typical traffic shape.
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.05,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Privacy: mask all text + block all media by default. We can selectively
        // allow specific surfaces later via `unmask`/`unblock` selectors.
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Don't send errors that come from the browser's own crash handlers
    // (network failures while offline, etc.) — too noisy.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
    ],
  });
}

export { Sentry };
